import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { DealerLayout } from "@/components/shared/DealerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import {
  getDealerDashboard, getMyMarketplaceOrders, getDealerStock, getMyReceivedSerials,
  getTrainingCompletions, getDealerAssignedLocations, getMyDealerDocuments,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { VerizonSalesSnapshotCard } from "@/components/verizon/VerizonSalesSnapshotCard";
import { getVzDealerSummary, fmtNum } from "@/lib/verizonSalesApi";
import { buildCards } from "@/lib/vzCards";
import { KpiGrid } from "@/components/verizon/KpiCallout";
import { DashboardFilterBar } from "@/components/shared/DashboardFilterBar";
import { motion } from "framer-motion";
import {
  DollarSign, Package, TrendingUp, Clock, ArrowRight, ArrowUpRight,
  ShoppingCart, Hash, Search, CheckCircle, XCircle, Loader2, Box,
  MapPin, FileText, GraduationCap, BookOpen, CircleCheck, Circle, Activity,
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";

const mockChartData = [
  { month: "Jul", amount: 1200 }, { month: "Aug", amount: 1800 },
  { month: "Sep", amount: 1500 }, { month: "Oct", amount: 2200 },
  { month: "Nov", amount: 2800 }, { month: "Dec", amount: 3200 },
];

const statusColors = {
  pending: "bg-amber-100 text-amber-700",
  processing: "bg-blue-100 text-blue-700",
  fulfilled: "bg-emerald-100 text-emerald-700",
  cancelled: "bg-red-100 text-red-700",
};

function OnboardingTracker({ steps }) {
  const completedCount = steps.filter((s) => s.done).length;
  const allDone = completedCount === steps.length;
  if (allDone) return null;

  const progress = Math.round((completedCount / steps.length) * 100);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
      <Card className="border-slate-200 border-l-4 border-l-teal" data-testid="onboarding-tracker">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading font-bold text-navy text-lg">Getting Started</h3>
              <p className="text-sm text-slate-500">{completedCount} of {steps.length} steps completed</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-teal">{progress}%</span>
            </div>
          </div>
          <div className="w-full h-2 bg-slate-100 rounded-full mb-5 overflow-hidden">
            <div className="h-full bg-teal rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {steps.map((step) => {
              const StepIcon = step.icon;
              return (
                <Link key={step.key} to={step.link} className="block" data-testid={`onboard-step-${step.key}`}>
                  <div className={`p-3 rounded-lg border transition-colors ${step.done ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200 hover:border-teal/40"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {step.done ? <CircleCheck className="w-4 h-4 text-emerald-500" /> : <Circle className="w-4 h-4 text-slate-300" />}
                      <StepIcon className={`w-4 h-4 ${step.done ? "text-emerald-500" : "text-slate-400"}`} />
                    </div>
                    <p className={`text-sm font-medium ${step.done ? "text-emerald-700" : "text-navy"}`}>{step.label}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{step.done ? "Completed" : step.desc}</p>
                  </div>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default function DealerDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [vzSummary, setVzSummary] = useState(null);
  const [filters, setFilters] = useState({ store: "all" });
  const vzStore = filters.store !== "all" ? vzSummary?.stores.find((s) => s.door_tsp === filters.store) : null;
  const scope = vzStore ? [vzStore] : (vzSummary?.stores || []);
  const { row1, row2, row3 } = buildCards({ scope, summaryTotals: vzSummary?.totals, qs: vzSummary?.quota_settings });
  const [orders, setOrders] = useState([]);
  const [serials, setSerials] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [serialSearch, setSerialSearch] = useState("");
  const [onboarding, setOnboarding] = useState({ locations: false, documents: false, training: false, marketplace: false });

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [dash, ords, prods, sers, completions, locations, docs] = await Promise.all([
          getDealerDashboard().catch(() => null),
          getMyMarketplaceOrders().catch(() => []),
          getDealerStock().catch(() => []),
          getMyReceivedSerials().catch(() => []),
          getTrainingCompletions().catch(() => []),
          getDealerAssignedLocations().catch(() => []),
          getMyDealerDocuments().catch(() => []),
        ]);
        setStats(dash);
        setOrders(ords);
        setProducts(prods);
        setSerials(sers);
        setOnboarding({
          locations: locations.length > 0,
          documents: (Array.isArray(docs) ? docs : docs?.documents || []).length > 0,
          training: completions.length > 0,
          marketplace: ords.length > 0,
        });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
    getVzDealerSummary().then(setVzSummary).catch(() => setVzSummary(null));
  }, []);

  const filteredSerials = serialSearch
    ? serials.filter((s) =>
        s.serial_number?.toLowerCase().includes(serialSearch.toLowerCase()) ||
        s.product_name?.toLowerCase().includes(serialSearch.toLowerCase())
      )
    : serials;

  return (
    <DealerLayout title={`Welcome, ${user?.first_name || "Dealer"}!`}>
      <div className="space-y-6" data-testid="dealer-dashboard">
        {vzSummary?.stores?.length > 0 && <DashboardFilterBar stores={vzSummary.stores} filters={filters} onChange={setFilters} />}
        {[row1, row2, row3].map((cards, row) => (
          <KpiGrid key={row} cards={cards} loading={loading} testId={`kpi-row-${row + 1}`} />
        ))}

        {user?.role === "dealer" && <VerizonSalesSnapshotCard fetcher={getVzDealerSummary} link={!user?.employee_id || (user?.dealer_permissions || []).includes("dealer_sales") ? "/dealer/verizon-sales" : null} testId="dealer-vz-snapshot" storeTsp={filters.store} title="Dealer Sales" />}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="hidden">
            <TabsTrigger value="overview">Overview</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="mt-6" forceMount>
            <div className="grid lg:grid-cols-3 gap-6">
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="lg:col-span-2">
                <Card className="border-slate-200">
                  <CardHeader className="pb-2"><CardTitle className="font-heading text-navy text-lg">Commission History</CardTitle></CardHeader>
                  <CardContent>
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={mockChartData}>
                          <defs><linearGradient id="colorAmount" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#00B2A9" stopOpacity={0.3} /><stop offset="95%" stopColor="#00B2A9" stopOpacity={0} /></linearGradient></defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="month" stroke="#64748b" fontSize={12} />
                          <YAxis stroke="#64748b" fontSize={12} tickFormatter={(v) => `$${v}`} />
                          <Tooltip contentStyle={{ backgroundColor: "#fff", border: "1px solid #e2e8f0", borderRadius: "8px" }} formatter={(value) => [`$${value}`, "Commission"]} />
                          <Area type="monotone" dataKey="amount" stroke="#00B2A9" strokeWidth={2} fillOpacity={1} fill="url(#colorAmount)" />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}>
                <Card className="border-slate-200 h-full">
                  <CardHeader className="pb-2"><CardTitle className="font-heading text-navy text-lg">Quick Actions</CardTitle></CardHeader>
                  <CardContent className="space-y-3">
                    <Link to="/dealer/marketplace"><Button variant="outline" className="w-full justify-between" data-testid="quick-action-marketplace"><span>Browse Marketplace</span><ArrowRight className="w-4 h-4" /></Button></Link>
                    <Link to="/dealer/orders"><Button variant="outline" className="w-full justify-between" data-testid="quick-action-orders"><span>View Orders</span><ArrowRight className="w-4 h-4" /></Button></Link>
                    <Link to="/dealer/commissions"><Button variant="outline" className="w-full justify-between" data-testid="quick-action-commissions"><span>Commission Details</span><ArrowRight className="w-4 h-4" /></Button></Link>
                    <Link to="/dealer/training"><Button variant="outline" className="w-full justify-between" data-testid="quick-action-training"><span>Training Center</span><ArrowRight className="w-4 h-4" /></Button></Link>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </DealerLayout>
  );
}
